import { Dispatch } from 'react';
import {
    EstateType,
    OwnerObjective,
    RegionCode,
    WantsToSellOwnerObjectives,
} from '@aviv-estimation-funnel/estimation-data-models/enums';
import { addError } from '@mfe-helpers/datadog';
import { formatStringWithPlusSign } from '@mfe-helpers/format';
import { ShortRegionCode, TimeToSell } from '@mfe-presentation/constants/sellerLeadValue';
import { ResultStatus } from '@mfe-presentation/constants/userVerification';
import { IAction } from '@mfe-types/context';
import { IStep } from '@mfe-types/form';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';

export const getIsSeller = (ownerObjective: OwnerObjective) => {
    return WantsToSellOwnerObjectives.includes(ownerObjective);
};

// Used for ImmoWelt portal only for seller lead value
const RegionCodeToShortCode: Record<string, ShortRegionCode> = {
    [RegionCode.Germany]: ShortRegionCode.DEU,
    [RegionCode.Austria]: ShortRegionCode.AUS,
    [RegionCode.Switzerland]: ShortRegionCode.SWT,
    [RegionCode.Belgium]: ShortRegionCode.BEL,
};

const ownerObjectiveToTimeToSell: Record<string, TimeToSell> = {
    [OwnerObjective.WantsToSellAsap]: TimeToSell.ASAP,
    [OwnerObjective.WantsToSellInLessThanThreeMonths]: TimeToSell.NextThreeMonth,
    [OwnerObjective.WantsToSellInLessThanSixMonths]: TimeToSell.NextSixMonth,
    [OwnerObjective.WantsToSellInLessThanOneYear]: TimeToSell.NextTwelveMonth,
    [OwnerObjective.WantsToSellInMoreThanOneYear]: TimeToSell.Later,
};

export const getShortPhoneNumberRegionCode = (
    PhoneNumberRegionCode: string,
): ShortRegionCode | '' => RegionCodeToShortCode[PhoneNumberRegionCode] || '';

export const getTimeToSell = (ownerObjective: string): TimeToSell | undefined =>
    ownerObjectiveToTimeToSell[ownerObjective] || undefined;

// TODO [WLEF-485] define it somewhere else
export const constructionPeriodValues: Record<string, any> = {
    before1870: {
        min: null,
        max: 1870,
        value: 1870,
    },
    '1871to1913': {
        min: 1871,
        max: 1913,
        value: 1892,
    },
    '1914to1944': {
        min: 1914,
        max: 1944,
        value: 1929,
    },
    '1945to1969': {
        min: 1945,
        max: 1969,
        value: 1957,
    },
    '1970to1979': {
        min: 1970,
        max: 1979,
        value: 1975,
    },
    '1980to1989': {
        min: 1980,
        max: 1989,
        value: 1985,
    },
    '1990to1999': {
        min: 1990,
        max: 1999,
        value: 1995,
    },
    '2000to2009': {
        min: 2000,
        max: 2009,
        value: 2005,
    },
    after2010: {
        min: 2010,
        max: null,
        value: 2015,
    },
    iDontKnow: {
        min: null,
        max: null,
        value: null,
    },
};

/*
TODO [WLEF-485] Fields with max value return a string but it should be a integer
 when it is sent for estimation creation. As this depends on the portal,
 it should not be manage here.
 */
const MAX_VALUE_FIELDS = [
    'numberOfRooms',
    'numberOfBathRooms',
    'floorNumber',
    'numberOfFloorsInBuilding',
    'privateGardensSurface',
    'balconiesAndTerracesSurface',
    'basementsSurface',
    'numberOfParkingLots',
    'numberOfIndoorParkingLots',
    'numberOfOutdoorParkingLots',
    'numberOfBedRooms',
];

const getFilteredForm = (form: Record<string, any>) => {
    return Object.fromEntries(
        Object.entries(form).filter(
            ([, value]) => typeof value !== 'undefined' && value !== '' && value !== null,
        ),
    );
};

const sanitizeLocationValues = (fieldValues: Record<string, any>) => {
    const form = { ...fieldValues };

    form.geoPlaceId = form.addressId || form.streetId || form.cityId;
    delete form.cityId;
    delete form.streetId;
    delete form.addressId;

    return form;
};

const formatSubmitFormError = (error: any) => {
    if (!error?.response?.data.phoneNumber) return ResultStatus.UnexpectedError;

    const {
        phoneNumber: [{ code }],
    } = error.response.data;

    if (code) {
        return code;
    }

    return ResultStatus.UnexpectedError;
};

export const getFormattedForm = (fieldValues: Record<string, any>, locale: TLocale) => {
    let form = { ...fieldValues };

    // TODO: [WLEF-485] formating must not be present here
    if (!form.hasAnOutdoorParking) {
        delete form.numberOfOutdoorParkingLots;
    }
    if (!form.hasAnIndoorParking) {
        delete form.numberOfIndoorParkingLots;
    }
    if (!form.hasABalconyOrTerrace) {
        delete form.balconiesAndTerracesSurface;
    }

    if (form.estateType === EstateType.Apartment) {
        delete form.hasABasement;
        delete form.basementsSurface;
        delete form.hasASwimmingPool;
        delete form.numberOfFacades;
        delete form.totalSiteSurface;

        if (form.constructionPeriod) {
            const constructionPeriod = form.constructionPeriod as string;
            form.constructionPeriodMin = constructionPeriodValues[constructionPeriod].min;
            form.constructionPeriodMax = constructionPeriodValues[constructionPeriod].max;
            form.constructionYear = constructionPeriodValues[constructionPeriod].value;
        }
    } else if (form.estateType === EstateType.House) {
        delete form.floorNumber;
        delete form.hasACellar;
        delete form.hasAPrivateGarden;
        delete form.privateGardensSurface;
        delete form.hasAnElevator;
        delete form.constructionPeriodMin;
        delete form.constructionPeriodMax;
        delete form.monthlyCondominiumFee;
    }
    MAX_VALUE_FIELDS.forEach((fieldName) => {
        if (form[fieldName]) {
            form[fieldName] = formatStringWithPlusSign(form[fieldName]);
        }
    });

    delete form.constructionPeriod;

    if (form.isAPrivatePerson === false) {
        delete form.isPropertyOwner;
        delete form.propertyMainUsage;
        delete form.ownerObjective;
        delete form.nonOwnerObjective;
    }

    if (form.isPropertyOwner) {
        delete form.nonOwnerObjective;
    } else {
        delete form.propertyMainUsage;
        delete form.ownerObjective;
    }

    form.hasAcceptedTermsAndConditions = true;
    form.language = locale.toUpperCase();

    if (typeof form.hasRejectedEmailMarketingCommunications === 'boolean') {
        form.hasAcceptedEmailMarketingCommunications =
            !form.hasRejectedEmailMarketingCommunications;
    }

    form = sanitizeLocationValues(form);
    return getFilteredForm(form);
};

const submitFormattedForm = async (
    fieldValues: Record<string, any>,
    config: IStep,
    name: string,
    locale: TLocale,
    dispatch?: Dispatch<IAction>,
) => {
    try {
        return await config[name].httpCalls?.submitForm(
            getFormattedForm(fieldValues, locale),
            dispatch,
        );
    } catch (error: any) {
        const submitError = formatSubmitFormError(error);
        addError(error, { payload: fieldValues });
        throw new Error(submitError);
    }
};

export default submitFormattedForm;
